import z from 'zod';
import { LeadStatusSchema, type LeadStatus } from './marketing/lead-schema';
import { formatClientName, formatPhoneNumber } from '../lib/client-utils';
import { AuditSchema } from './settings/audit-schema';

/**
 * ESQUEMA UNIFICADO DE CLIENTE / PROSPECTO (LEAD)
 * Ubicación: Subcolección owners/{ownerId}/clients
 */
export const ClientSchema = z.object({
  // 1. INFORMACIÓN DE CONTACTO
  name: z
    .string()
    .min(2, 'El nombre es muy corto')
    .transform((val) => formatClientName(val)),

  phone: z
    .string()
    .min(7, 'El teléfono es inválido')
    .transform((val) => formatPhoneNumber(val)),

  email: z.string().email().optional().nullable(),

  // 2. EMBUDO DE VENTAS (MARKETING)
  status: LeadStatusSchema.default('CREATED'),
  
  // Origen del prospecto
  source: z.enum([
    'manual',
    'web_form',
    'meta_ad',
    'catalog',
    'academy',
    'csv_import',
    'whatsapp',
    'floating_contact',
    'public_page',
  ]).default('manual'),
  
  // Contexto de captación (Qué estaba viendo cuando dejó sus datos)
  context: z.object({
    type: z.enum(['product', 'course', 'general']),
    id: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    price: z.number().optional(),
    sku: z.string().nullable().optional(),
    groupId: z.string().nullable().optional(),
    assignedPixelId: z.string().nullable().optional(),
  }).optional(),

  // 3. ATRIBUCIÓN (META ADS)
  marketing_data: z.object({
    campaignId: z.string().optional(),
    campaignName: z.string().nullable().optional(),
    adsetId: z.string().optional(),
    adsetName: z.string().nullable().optional(),
    adId: z.string().optional(),
    adName: z.string().nullable().optional(),
    metaAdId: z.string().optional(),
    metaAdsetId: z.string().optional(),
    fbclid: z.string().optional(),
    fbp: z.string().optional(),
    fbc: z.string().optional(),
    utm_source: z.string().optional(),
    utm_medium: z.string().optional(),
    utm_campaign: z.string().optional(),
    utm_content: z.string().optional(),
    utm_term: z.string().optional(),
    platform: z.string().optional(),
    landing_url: z.string().optional(),
    referrer: z.string().optional(),
    source_product_url: z.string().optional(),
    client_ip: z.string().optional(),
    user_agent: z.string().optional(),
    userAgent: z.string().optional(),
    // Píxel específico asignado por el Admin a este lead/producto
    assignedPixelId: z.string().optional(),
  }).optional(),

  // 4. CONTADORES FINANCIEROS (LTV)
  total_spent_accounting: z.number().default(0),
  last_interaction_at: z.any().nullable().optional(),

  // 5. NOTAS Y PREFERENCIAS
  notes: z.string().nullable().optional(),

  // 6. ACADEMIA
  studentUid: z.string().optional(),
  academy: z.object({
    isStudent: z.boolean().default(false),
    authorizedCourses: z.array(z.string()).default([]),
    lastActive: z.any().optional(),
    progress: z.record(
      z.string(),
      z.object({
        completedLessons: z.array(z.string()).default([]),
        lastAccessedAt: z.string().nullable().optional(),
      }),
    ).default({}),
  }).optional(),

  // 7. AUDITORÍA
  audit: AuditSchema,
});

// --- EXPORTACIÓN DE TIPOS ---

/** * ClientInput: Lo que usas en el formulario de "Nuevo Cliente".
 * Omitimos la auditoría y los campos que el Service calcula solo.
 */
export const ClientInputSchema = ClientSchema.omit({
  audit: true,
  total_spent_accounting: true,
});

export type ClientInput = z.infer<typeof ClientInputSchema>;

/** El objeto completo en Firestore */
export type Client = z.infer<typeof ClientSchema>;

/** El objeto que manejas en el Frontend (con el ID inyectado) */
export type ClientWithId = Client & { id: string };

/** Tipo auxiliar para el addDoc en el Service */
export type ClientCreate = Omit<Client, 'id'>;

export const CLIENT_LIMIT_FREE = 5;
export const CLIENT_LIMIT_BUSINESS = 10000; // O "Unlimited" en lógica
